import { Box, Button, Flex, Text } from '@mantine/core';
import { useAssignSigner } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useAssignSigner';
import { useUnassignSigner } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useUnassignSigner';
import { useClaimSigner } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useClaimSigner';
import { isClaimableDocumentConfig } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/SignersTab.claim.utils';
import { isEidEasySigningDocument } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/SignersTab.eidEasy.utils';
import { isAuthorisedSignatorySupported } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/SignersTab.authorisedSignatory.utils';
import { useSignWetInkDocument } from '@dealstack/frontend/shared/modules/document/signing/hooks/useSignWetInkDocument';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useProgrammeUsers } from '@dealstack/frontend/app/pages/Project/MIP/hooks';
import {
	DocumentSigningRoleType,
	DocumentSigningSecurityLevel,
	DocumentWorkflowInstanceStatus,
	InvestmentProgrammeDocumentDetailsRes,
	SignatureType,
} from '@dealstack/shared/lib/generated';
import { FileExtension } from '@dealstack/shared/lib/common/dataExchange/common';
import { formatUserFullName } from '@dealstack/shared/lib/format/user';
import { normaliseEmail } from '@dealstack/shared/lib/format/emailHelpers';
import { getValidFileNameForDownload } from '@dealstack/shared/lib/common/file/file';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useDownloadFileUsingFileStorageUrl } from '@dealstack/frontend/shared/modules/document/file/hooks/useDownloadFileUsingFileStorageUrl';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { selectUserId } from '@dealstack/frontend/app/redux/user.selectors';
import { useAppSelector } from '@dealstack/frontend/app/hooks/redux';
import { WetInkSigningModal } from '@dealstack/frontend/shared/components/WetInkSigningModal/WetInkSigningModal';
import { getFileId } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/Documents.utils';
import {
	PendingDocumentActionType,
	type PendingDocumentAction,
	type PendingSignerAssignmentAction,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/usePendingDocumentAction.types';
import { SignerGroupSection } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/SignerGroupSection';
import { hasOutstandingSignatures } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/RemoveSigner/removeSigner.utils';
import {
	buildOccupiedEmailSet,
	canCurrentUserClaimCounterpartySlot,
	createSignerWithRepresentativeGroups,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/SignerTab.utils';
import { findClaimedGroupReadyToSign } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/claimAndSign.utils';
import { usePendingClaimSign } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/usePendingClaimSign';
import {
	getApplyDateAction,
	getEidEasySigningPhase,
	SignersTabView,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/SignersTab.utils';
import { useEidEasySign } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useEidEasySign';
import type { EidEasyCompleteRes } from '@dealstack/shared/lib/generated';
import type { SignatureSuccessDetails } from '@dealstack/frontend/shared/components/EidEasySigningModal/steps/SignatureSuccessConfirmation/SignatureSuccessConfirmation.types';
import { SigningPanel } from '@dealstack/frontend/shared/components/EidEasySigningModal/SigningPanel';
import { EidEasySignStatus } from '@dealstack/frontend/shared/components/EidEasySigningModal/EidEasySigningModal.types';
import { WitnessSigningStep } from '@dealstack/frontend/shared/components/EidEasySigningModal/steps/WitnessSigningStep';
import { WitnessSuccessStep } from '@dealstack/frontend/shared/components/EidEasySigningModal/steps/WitnessSuccessStep';
import { useWitnessBeforeSigning } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useWitnessBeforeSigning';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { methodsForSecurityLevel } from '@dealstack/shared/lib/common/eideasy/methodsForSecurityLevel';
import { webIsProduction } from '@dealstack/frontend/shared/environment';
import { useInviteWitness } from '@dealstack/frontend/investor/pages/Investment/common/DocumentWorkflowContracts/hooks/useInviteWitness';
import { useInviteAuthorisedSignatory } from '@dealstack/frontend/investor/pages/Investment/common/DocumentWorkflowContracts/hooks/useInviteAuthorisedSignatory';
import { resendSigningInvitation } from '@dealstack/frontend/shared/dataExchange/services/shareholder/investment.service';
import { WitnessEmailModal } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/WitnessEmailModal';
import { AuthorisedSignatoryModal } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/AuthorisedSignatoryModal';
import { CancelAuthorisedSignatoryModal } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/CancelAuthorisedSignatoryModal';
import { EmptyState } from '@dealstack/frontend/shared/components/EmptyState/EmptyState.tsx';
import { DocumentSignersBanner } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/DocumentSignersBanner';
import { ApplyDateModal } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/ApplyDateModal';
import { AddInvestorAction } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/AddInvestor/AddInvestorAction';
import { AddCounterpartyAction } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/AddCounterparty/AddCounterpartyAction';
import { toAssignableUsers } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/AddCounterparty/AddCounterpartyModal.utils';
import { RemoveSignerAction } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/RemoveSigner/RemoveSignerAction';
import type { RemovalTarget } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/RemoveSigner/RemoveSignerModal.types';
import {
	getGroupProgress,
	getDocumentProgress,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/documentSigningProgress';
import type { GroupProgress } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/documentSigningProgress.types';
import type {
	SigningHeaderContext,
	SigningContext,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/SignersTab.types';

interface Props {
	dealId: string;
	programmeId: string;
	documentWorkflowInstanceId: string;
	documentDetails: InvestmentProgrammeDocumentDetailsRes;
	isActionPending: boolean;
	beginPendingAction: (action: PendingDocumentAction) => void;
	clearPendingAction: () => void;
	refetchDocument: () => void;
	pendingSignerAssignmentAction: PendingSignerAssignmentAction | null;
	onSigningHeaderChange?: (header: SigningHeaderContext | null) => void;
	onDateApplied: () => void;
}

// TODO refactor this component, it's doing too much and has too many responsibilities. The signing logic should be extracted to a separate component that is rendered when the user initiates signing, rather than trying to handle it all within this component which is also responsible for rendering the list of signers and handling assignment/unAssignment.
export const SignersTabContent = ({
	dealId,
	programmeId,
	documentWorkflowInstanceId,
	documentDetails,
	isActionPending,
	beginPendingAction,
	clearPendingAction,
	refetchDocument,
	pendingSignerAssignmentAction,
	onSigningHeaderChange,
	onDateApplied,
}: Props) => {
	const [assigningSlot, setAssigningSlot] = useState<{
		roleId: string;
		slotIndex: number;
	} | null>(null);
	const [unAssigningRoleId, setUnAssigningRoleId] = useState<string | null>(null);
	const [signingModalOpen, setSigningModalOpen] = useState(false);
	const [selectedFile, setSelectedFile] = useState<File | undefined>();
	const [signingContext, setSigningContext] = useState<SigningContext | null>(null);
	const [witnessModalOpen, setWitnessModalOpen] = useState(false);
	const [witnessParentSignatureId, setWitnessParentSignatureId] = useState<string | null>(null);
	const [witnessInitialEmail, setWitnessInitialEmail] = useState<string | undefined>();
	const [applyDateModalOpen, setApplyDateModalOpen] = useState(false);
	const [authorisedSignatoryModalOpen, setAuthorisedSignatoryModalOpen] = useState(false);
	const [authorisedSignatorySignatureId, setAuthorisedSignatorySignatureId] = useState<
		string | null
	>(null);
	const [authorisedSignatoryInitialEmail, setAuthorisedSignatoryInitialEmail] = useState<
		string | undefined
	>();
	const [authorisedSignatoryInitialFirstName, setAuthorisedSignatoryInitialFirstName] = useState<
		string | undefined
	>();
	const [authorisedSignatoryInitialLastName, setAuthorisedSignatoryInitialLastName] = useState<
		string | undefined
	>();
	const [cancelSignatoryTarget, setCancelSignatoryTarget] = useState<{
		signatureId: string;
		signatoryName: string;
	} | null>(null);
	const [activeSigner, setActiveSigner] = useState<{
		effectiveSecurityLevel: DocumentSigningSecurityLevel;
		roleType: DocumentSigningRoleType;
		displayName: string;
	} | null>(null);
	const [completionResult, setCompletionResult] = useState<EidEasyCompleteRes | null>(null);
	const [isDownloadingSignedPdf, setIsDownloadingSignedPdf] = useState(false);
	// True while an assign/unassign is still settling. Gates the Sign button so
	// it cannot open against a signing URL or file the backend is mid-way through
	// replacing.
	const isSignerUpdatePending = pendingSignerAssignmentAction !== null;
	const [pendingClaimSignRoleId, setPendingClaimSignRoleId] =
		usePendingClaimSign(isSignerUpdatePending);
	const currentUserId = useAppSelector(selectUserId);

	// Every signing decision below must use this: wet-ink and provider-less templates must not be
	// routed into the eID Easy flow.
	const isEidEasyDocument = isEidEasySigningDocument(documentDetails.eSignProvider);

	const tMipManage = useTranslationMipManage();

	const { claimingSignerId, onClaimSigner } = useClaimSigner({
		dealId,
		programmeId,
		documentWorkflowInstanceId,
		refetchDocument,
		onClaimed: (signerId) =>
			beginPendingAction({
				action: PendingDocumentActionType.CLAIM_GENERATION,
				signerId,
			}),
	});

	// Claim & sign on behalf is eID-Easy only (mirrors the backend
	// isClaimableSigningConfig); wet-ink documents go through the investor
	// uploading a signed copy instead.
	const claimEnabledForDocument = isClaimableDocumentConfig(
		documentDetails.signatureType,
		documentDetails.eSignProvider
	);

	// Authorised signatory functionality was only built for eID Easy workflows, so the
	// invite / resend controls stay hidden for anything else.
	const authorisedSignatoryEnabledForDocument = isAuthorisedSignatorySupported(
		documentDetails.eSignProvider
	);

	const fileId = getFileId({
		documentDetails,
		currentUserId: currentUserId ?? '',
	});
	const { handleSigningDocumentWorkflow, wetInkSignDocumentWorkflowIsLoading } =
		useSignWetInkDocument({
			dealId,
			programmeId,
			documentWorkflowInstanceId,
		});

	const {
		startSign: eidEasyStartSign,
		selectMethod: eidEasySelectMethod,
		goBack: eidEasyGoBack,
		confirmSmartId: eidEasyConfirmSmartId,
		confirmFreja: eidEasyConfirmFreja,
		confirmOtp: eidEasyConfirmOtp,
		submitOtpCode: eidEasySubmitOtpCode,
		resendOtpCode: eidEasyResendOtpCode,
		otpVerifying: eidEasyOtpVerifying,
		otpCodeError: eidEasyOtpCodeError,
		confirmSignature: eidEasyConfirmSignature,
		onSignatureConfirmed: eidEasyOnSignatureConfirmed,
		reset: eidEasyReset,
		status: eidEasyStatus,
		challengeCode: eidEasyChallengeCode,
		error: eidEasyError,
		selectedMethod: eidEasySelectedMethod,
		isSigningInProgress: eidEasyInProgress,
		otpChannel: eidEasyOtpChannel,
		otpMaskedContact: eidEasyOtpMaskedContact,
	} = useEidEasySign({
		dealId,
		programmeId,
		documentWorkflowInstanceId,
		onComplete: (result) => {
			witnessBeforeSigning.onSigningComplete();
			beginPendingAction({
				action: PendingDocumentActionType.DOCUMENT_REFRESH,
				previousUpdatedAt: documentDetails.updatedAt ?? null,
			});
			setCompletionResult(result ?? null);
		},
	});

	const {
		begin: beginSigning,
		reset: resetWitness,
		backToWitness,
		...witnessBeforeSigning
	} = useWitnessBeforeSigning({
		dealId,
		programmeId,
		documentWorkflowInstanceId,
		startSign: eidEasyStartSign,
	});

	const { data: programmeUsers, isLoading: isLoadingUsers } = useProgrammeUsers({
		programmeId,
	});

	// A slot created with a user already on it triggers the same asynchronous signature-sheet
	// regeneration a manual assignment does, so it waits the same way — otherwise the panel renders
	// the new signer before its file exists. An unassigned slot has nothing to wait for.
	const handleCounterpartyAdded = useCallback(
		({ roleId, assignedUserIds }: { roleId: string; assignedUserIds: string[] }) => {
			const [firstAssignedUserId] = assignedUserIds;
			if (!firstAssignedUserId) {
				refetchDocument();
				return;
			}
			beginPendingAction({
				action: PendingDocumentActionType.ASSIGN,
				roleId,
				userId: firstAssignedUserId,
			});
		},
		[beginPendingAction, refetchDocument]
	);

	// Derived where the users are fetched, not in an effect: the picker only needs id + label.
	const counterpartyAssignableUsers = useMemo(
		() => toAssignableUsers(programmeUsers ?? []),
		[programmeUsers]
	);

	const assignSignerMutation = useAssignSigner({
		dealId,
		programmeId,
		documentWorkflowInstanceId,
	});

	const unassignSignerMutation = useUnassignSigner({
		dealId,
		programmeId,
		documentWorkflowInstanceId,
	});

	const { downloadFile } = useDownloadFileUsingFileStorageUrl();

	// Server-computed: a counterparty may sign once the document's file is final (see
	// isReadyForCounterpartySigning). Not derivable client-side — it depends on whether
	// the document needs generating at all.
	const isCounterpartySigningBlocked = !documentDetails.readyForCounterpartySigning;

	const inviteWitnessMutation = useInviteWitness();
	const inviteAuthorisedSignatoryMutation = useInviteAuthorisedSignatory();
	const [resendingSignatureId, setResendingSignatureId] = useState<string | null>(null);

	const resendInvitation = async (
		signatureId: string,
		successTitle: string,
		errorTitle: string
	) => {
		setResendingSignatureId(signatureId);
		try {
			await resendSigningInvitation({
				dealId,
				programmeId,
				documentWorkflowInstanceId,
				signatureId,
			});
			showToastV2({ title: successTitle, state: 'success' });
		} catch {
			showToastV2({ title: errorTitle, state: 'error' });
		} finally {
			setResendingSignatureId(null);
		}
	};

	const handleResendWitnessInvitation = (signatureId: string) =>
		resendInvitation(
			signatureId,
			tMipManage('documents_page.toast.success.witness_resent'),
			tMipManage('documents_page.toast.error.witness_resend_failed')
		);

	const handleResendAuthorisedSignatoryInvitation = (signatureId: string) =>
		resendInvitation(
			signatureId,
			tMipManage('documents_page.toast.success.witness_resent'),
			tMipManage('documents_page.toast.error.authorised_signatory_resend_failed')
		);

	const handleInviteWitness = (parentSignatureId: string) => {
		const signer = documentDetails.groups
			.flatMap((g) => g.signers)
			.find((s) => s.signatureId === parentSignatureId);
		const existingWitness = signer?.witnesses?.[0];
		setWitnessParentSignatureId(parentSignatureId);
		setWitnessInitialEmail(existingWitness?.email);
		setWitnessModalOpen(true);
	};

	const handleWitnessSubmit = (email: string) => {
		if (!witnessParentSignatureId) {
			return;
		}
		inviteWitnessMutation.mutate(
			{
				dealId,
				programmeId,
				documentWorkflowInstanceId,
				parentSignatureId: witnessParentSignatureId,
				witnessEmail: email,
			},
			{
				onSuccess: () => {
					setWitnessModalOpen(false);
					setWitnessParentSignatureId(null);
					showToastV2({
						title: tMipManage('documents_page.toast.success.witness_invited'),
						state: 'success',
					});
					beginPendingAction({
						action: PendingDocumentActionType.DOCUMENT_REFRESH,
						previousUpdatedAt: documentDetails.updatedAt ?? null,
					});
				},
				onError: () => {
					showToastV2({
						title: tMipManage('documents_page.toast.error.witness_invite_failed'),
						state: 'error',
					});
				},
			}
		);
	};

	const handleCancelAuthorisedSignatory = (signatureId: string, signatoryName: string) => {
		setCancelSignatoryTarget({ signatureId, signatoryName });
	};

	const handleInviteAuthorisedSignatory = (signatureId: string) => {
		const signer = documentDetails.groups
			.flatMap((g) => g.signers)
			.find((s) => s.signatureId === signatureId);
		setAuthorisedSignatorySignatureId(signatureId);
		setAuthorisedSignatoryInitialEmail(signer?.email ?? undefined);
		setAuthorisedSignatoryInitialFirstName(signer?.firstName ?? undefined);
		setAuthorisedSignatoryInitialLastName(signer?.lastName ?? undefined);
		setAuthorisedSignatoryModalOpen(true);
	};

	const handleAuthorisedSignatorySubmit = (
		email: string,
		firstName: string,
		lastName: string
	) => {
		if (!authorisedSignatorySignatureId) {
			return;
		}
		inviteAuthorisedSignatoryMutation.mutate(
			{
				path: { dealId, programmeId, documentWorkflowInstanceId },
				body: {
					signatureId: authorisedSignatorySignatureId,
					email,
					firstName,
					lastName,
				},
			},
			{
				onSuccess: () => {
					setAuthorisedSignatoryModalOpen(false);
					setAuthorisedSignatorySignatureId(null);
					showToastV2({
						title: tMipManage(
							'documents_page.toast.success.authorised_signatory_invited'
						),
						state: 'success',
					});
					beginPendingAction({
						action: PendingDocumentActionType.DOCUMENT_REFRESH,
						previousUpdatedAt: documentDetails.updatedAt ?? null,
					});
				},
			}
		);
	};

	const signerWithRepresentativeGroups = useMemo(
		() => createSignerWithRepresentativeGroups(documentDetails.groups),
		[documentDetails.groups]
	);

	const [removalTarget, setRemovalTarget] = useState<RemovalTarget | null>(null);

	// Document-wide, not per-group: the backend refuses a removal that would leave the instance
	// with no signature requirements, or no investor, and a group only ever sees its own slice.
	const documentSignerCount = useMemo(
		() => documentDetails.groups.reduce((total, group) => total + group.signers.length, 0),
		[documentDetails.groups]
	);
	const documentPartyCount = useMemo(
		() =>
			documentDetails.groups
				.filter((group) => group.groupType === DocumentSigningRoleType.PARTY)
				.reduce((total, group) => total + group.signers.length, 0),
		[documentDetails.groups]
	);

	// The backend refuses a removal on a document with nothing left to sign, so the menu item
	// must not offer one — document-wide, as that gate is.
	const documentHasOutstandingSignatures = useMemo(
		() => hasOutstandingSignatures(documentDetails.groups),
		[documentDetails.groups]
	);

	const documentProgress = useMemo(
		() => getDocumentProgress(documentDetails.groups),
		[documentDetails.groups]
	);

	const applyDateAction = getApplyDateAction({
		requiresPostDating: documentDetails.requiresPostDating,
		dateAppliedAt: documentDetails.dateAppliedAt,
		workflowStatus: documentDetails.workflowStatus,
	});

	// Once the workflow reaches COMPLETED the document is fully signed, so the
	// simple-sign / witness overrides are moot and their toggles are hidden.
	const documentFullySigned =
		documentDetails.workflowStatus === DocumentWorkflowInstanceStatus.COMPLETED;

	const groupProgressMap = useMemo(() => {
		const map = new Map<string, GroupProgress>();
		for (const group of signerWithRepresentativeGroups) {
			map.set(group.roleId, getGroupProgress(group));
		}
		return map;
	}, [signerWithRepresentativeGroups]);

	const getGroupStatus = (roleId: string) => {
		const progress = groupProgressMap.get(roleId);
		if (!progress) {
			return undefined;
		}
		if (progress.isComplete) {
			return {
				label: tMipManage('documents_page.group_status.complete'),
				tone: 'complete' as const,
			};
		}
		return {
			label: tMipManage('documents_page.group_status.outstanding', {
				count: progress.outstandingCount,
			}),
			tone: 'outstanding' as const,
		};
	};

	const handleClaimSlot = (roleId: string) => {
		if (!currentUserId) {
			return;
		}
		setPendingClaimSignRoleId(roleId);
		handleAssignConfirm(roleId, currentUserId);
	};

	const openWetInkSigningModalForGroup = useCallback((groupName: string) => {
		setSigningContext({
			kind: 'COUNTERPARTY',
			roleName: groupName,
		});
		setSigningModalOpen(true);
	}, []);

	const handleSignClickForGroup = useCallback(
		(group: InvestmentProgrammeDocumentDetailsRes['groups'][number]) => {
			if (!currentUserId) {
				return;
			}

			const currentUserSigner = group.signers.find(
				(signer) => signer.assigned && signer.userId === currentUserId
			);

			if (!currentUserSigner) {
				showToastV2({
					title: tMipManage('documents_page.toast.error.signer_not_found'),
					state: 'error',
				});
				return;
			}

			if (documentDetails.signatureType === SignatureType.ELECTRONIC) {
				if (isEidEasyDocument) {
					if (!currentUserSigner.signatureId) {
						showToastV2({
							title: tMipManage('documents_page.toast.error.esign_url_not_available'),
							state: 'error',
						});
						return;
					}
					setActiveSigner({
						effectiveSecurityLevel: currentUserSigner.effectiveSecurityLevel,
						roleType: group.groupType,
						displayName: formatUserFullName({
							firstName: currentUserSigner.firstName ?? null,
							lastName: currentUserSigner.lastName ?? null,
						}),
					});
					beginSigning({
						signatureId: currentUserSigner.signatureId,
						requiresWitness: !!currentUserSigner.requiresWitness,
						witnessEmail: currentUserSigner.witnesses?.[0]?.email,
					});
					return;
				}

				showToastV2({
					title: tMipManage('documents_page.toast.error.esign_url_not_available'),
					state: 'error',
				});
				return;
			}

			if (documentDetails.signatureType === SignatureType.WET_INK) {
				openWetInkSigningModalForGroup(group.groupName);
			}
		},
		[
			currentUserId,
			documentDetails.signatureType,
			isEidEasyDocument,
			beginSigning,
			openWetInkSigningModalForGroup,
			tMipManage,
		]
	);

	// Same gates as the manual Sign button: don't clobber an open signing flow,
	// and don't start a counterparty signature still blocked on party-side signers.
	const autoSignBlocked =
		signingModalOpen || !!activeSigner || eidEasyInProgress || isCounterpartySigningBlocked;

	// Claim-and-sign: open the flow once the just-claimed signer is ready.
	// Electronic readiness lands with the post-claim refetch (synchronous
	// backend); wet-ink ALL_PARTICIPANTS regenerates the file async, so its
	// readiness lands on the SSE-driven refetch that settles the pending action.
	useEffect(() => {
		const readyGroup = findClaimedGroupReadyToSign({
			pendingClaimSignRoleId,
			isSignerUpdatePending,
			groups: documentDetails.groups,
			currentUserId: currentUserId ?? undefined,
			documentSignatureType: documentDetails.signatureType,
			documentSigningScope: documentDetails.signingScope,
			isEidEasyDocument,
		});
		if (!readyGroup) {
			return;
		}
		setPendingClaimSignRoleId(null);
		if (!autoSignBlocked) {
			handleSignClickForGroup(readyGroup);
		}
	}, [
		pendingClaimSignRoleId,
		isSignerUpdatePending,
		documentDetails.groups,
		documentDetails.signatureType,
		documentDetails.signingScope,
		currentUserId,
		isEidEasyDocument,
		autoSignBlocked,
		handleSignClickForGroup,
		setPendingClaimSignRoleId,
	]);

	const handleSignClaimedSigner = (
		signer: InvestmentProgrammeDocumentDetailsRes['groups'][number]['signers'][number],
		signingRoleType: DocumentSigningRoleType,
		group: InvestmentProgrammeDocumentDetailsRes['groups'][number]
	) => {
		if (!currentUserId || signer.claimedByUserId !== currentUserId) {
			return;
		}

		if (!signer.signatureId) {
			showToastV2({
				title: tMipManage('documents_page.toast.error.esign_url_not_available'),
				state: 'error',
			});
			return;
		}

		if (documentDetails.signatureType === SignatureType.ELECTRONIC) {
			if (isEidEasyDocument) {
				setActiveSigner({
					effectiveSecurityLevel: signer.effectiveSecurityLevel,
					roleType: signingRoleType,
					displayName: signer.claimedByName ?? '',
				});
				beginSigning({
					signatureId: signer.signatureId,
					requiresWitness: !!signer.requiresWitness,
					witnessEmail: signer.witnesses?.[0]?.email,
				});
				return;
			}

			// Electronic but not eID Easy — claiming is restricted to eID Easy, so this
			// path should not normally be reached.
			showToastV2({
				title: tMipManage('documents_page.toast.error.esign_url_not_available'),
				state: 'error',
			});
			return;
		}

		if (documentDetails.signatureType === SignatureType.WET_INK) {
			if (!signer.investorId) {
				showToastV2({
					title: tMipManage('documents_page.toast.error.signer_not_found'),
					state: 'error',
				});
				return;
			}
			setSigningContext({
				kind: 'CLAIMED_ON_BEHALF',
				roleName: group.groupName,
				investorId: signer.investorId,
				signingRoleType,
			});
			setSigningModalOpen(true);
		}
	};

	const handleAssignClick = (roleId: string, slotIndex: number) => {
		setUnAssigningRoleId(null);
		setAssigningSlot({ roleId, slotIndex });
	};

	const handleUnassignClick = (roleId: string) => {
		setAssigningSlot(null);
		setUnAssigningRoleId(roleId);
	};

	const handleAssignSuccess = () => {
		setAssigningSlot(null);
	};

	const handleAssignCancel = () => {
		setAssigningSlot(null);
	};

	const handleUnassignSuccess = () => {
		setUnAssigningRoleId(null);
	};

	const handleUnassignCancel = () => {
		setUnAssigningRoleId(null);
	};

	const handleAssignConfirm = (roleId: string, userId: string) => {
		const targetGroup = documentDetails.groups.find((g) => g.roleId === roleId);
		const previousSigner =
			targetGroup?.signers.find((s) => s.userId === userId) ??
			targetGroup?.signers.find((s) => s.userId === null) ??
			targetGroup?.signers.find((s) => !s.assigned);

		beginPendingAction({
			action: PendingDocumentActionType.ASSIGN,
			roleId,
			userId,
			previousSignerFileId: previousSigner?.fileId ?? null,
		});

		assignSignerMutation.mutate(
			{
				roleId,
				userId,
			},
			{
				onSuccess: () => handleAssignSuccess(),
				onError: () => {
					clearPendingAction();
					// Only drop the pending claim-and-sign if it was THIS
					// assignment — an unrelated assign failing must not cancel
					// a claim that is still in flight.
					setPendingClaimSignRoleId((prev) => (prev === roleId ? null : prev));
				},
			}
		);
	};

	const handleUnassignConfirm = (roleId: string, userId: string) => {
		const previousSigner = documentDetails.groups
			.find((g) => g.roleId === roleId)
			?.signers.find((s) => s.userId === userId);

		beginPendingAction({
			action: PendingDocumentActionType.UNASSIGN,
			roleId,
			userId,
			previousSignerFileId: previousSigner?.fileId ?? null,
		});

		unassignSignerMutation.mutate(
			{
				roleId,
				userId,
			},
			{
				onSuccess: () => handleUnassignSuccess(),
				onError: () => {
					clearPendingAction();
				},
			}
		);
	};

	const handleDownloadContract = async () => {
		if (!documentDetails) {
			return;
		}
		if (!fileId) {
			showToastV2({
				title: tMipManage('documents_page.toast.error.document_not_available_for_download'),
				state: 'error',
			});
			return;
		}

		const fileNameForDownload = getValidFileNameForDownload(
			documentDetails.fileName ?? 'document',
			FileExtension.pdf
		);

		await downloadFile({
			fileId,
			dealId,
			fileName: fileNameForDownload,
		});
	};

	const handleSave = async () => {
		if (!selectedFile || !signingContext) {
			showToastV2({
				title: tMipManage('documents_page.toast.error.missing_file_or_role'),
				state: 'error',
			});
			return;
		}

		const signingRoleType =
			signingContext.kind === 'COUNTERPARTY'
				? DocumentSigningRoleType.COUNTERPARTY
				: signingContext.signingRoleType;

		const investorId =
			signingContext.kind === 'CLAIMED_ON_BEHALF' ? signingContext.investorId : undefined;

		const selectedRoleName =
			signingContext.kind === 'COUNTERPARTY' ? signingContext.roleName : undefined;

		await handleSigningDocumentWorkflow({
			selectedFile,
			signingRoleType,
			selectedRoleName,
			investorId,
			onSuccess: () => {
				beginPendingAction({
					action: PendingDocumentActionType.DOCUMENT_REFRESH,
					previousUpdatedAt: documentDetails.updatedAt ?? null,
				});
				setSigningModalOpen(false);
				setSelectedFile(undefined);
				setSigningContext(null);
			},
		});
	};

	const handleModalClose = () => {
		setSigningModalOpen(false);
		setSelectedFile(undefined);
		setSigningContext(null);
	};

	const {
		view,
		canGoBack: canGoBackFromSigning,
		isOnSubStep,
	} = getEidEasySigningPhase(eidEasyStatus);

	const exitSigningToList = useCallback(() => {
		setActiveSigner(null);
		setCompletionResult(null);
		eidEasyReset();
		resetWitness();
	}, [eidEasyReset, resetWitness]);

	const handleDownloadSignedPdf = useCallback(async () => {
		const signedFileId = completionResult?.fileId ?? fileId;
		if (!signedFileId) {
			showToastV2({
				title: tMipManage('documents_page.toast.error.document_not_available_for_download'),
				state: 'error',
			});
			return;
		}
		const fileNameForDownload = getValidFileNameForDownload(
			documentDetails.fileName ?? 'document',
			FileExtension.pdf
		);
		setIsDownloadingSignedPdf(true);
		try {
			await downloadFile({ fileId: signedFileId, dealId, fileName: fileNameForDownload });
		} finally {
			setIsDownloadingSignedPdf(false);
		}
	}, [completionResult, fileId, documentDetails.fileName, downloadFile, dealId, tMipManage]);

	const signingSuccessDetails: SignatureSuccessDetails | undefined = activeSigner
		? {
				signerName: activeSigner.displayName || null,
				signingMethod: completionResult?.signingMethod ?? null,
				signedAt: completionResult?.signedAt ?? null,
				session: completionResult?.session,
				isDownloading: isDownloadingSignedPdf,
				onDownloadSignedPdf: () => {
					void handleDownloadSignedPdf();
				},
			}
		: undefined;

	const isWitnessStepActive = !!activeSigner && witnessBeforeSigning.isWitnessStepActive;
	const witnessRequired = witnessBeforeSigning.requiresWitness;

	const handleOuterBack = useCallback(() => {
		if (isOnSubStep) {
			eidEasyGoBack();
		} else if (witnessRequired && !isWitnessStepActive) {
			eidEasyReset();
			backToWitness();
		} else {
			exitSigningToList();
		}
	}, [
		isOnSubStep,
		eidEasyGoBack,
		eidEasyReset,
		witnessRequired,
		backToWitness,
		isWitnessStepActive,
		exitSigningToList,
	]);

	useEffect(() => {
		if ((view === SignersTabView.SIGNING || isWitnessStepActive) && activeSigner) {
			onSigningHeaderChange?.({
				signerDisplayName: activeSigner.displayName,
				canGoBack: canGoBackFromSigning,
				onBack: handleOuterBack,
			});
		} else {
			onSigningHeaderChange?.(null);
		}
	}, [
		view,
		isWitnessStepActive,
		activeSigner,
		canGoBackFromSigning,
		handleOuterBack,
		onSigningHeaderChange,
	]);

	if (!documentDetails.groups?.length) {
		return (
			<EmptyState
				icon="es-post-box"
				iconSize="sm"
				description={tMipManage('documents_page.no_signers')}
			/>
		);
	}

	if (isWitnessStepActive) {
		return (
			<WitnessSigningStep
				defaultEmail={witnessBeforeSigning.witnessEmail}
				onContinue={witnessBeforeSigning.continueToSign}
			/>
		);
	}

	if (
		view === SignersTabView.SIGNING &&
		activeSigner &&
		eidEasyStatus === EidEasySignStatus.DONE &&
		witnessRequired
	) {
		return (
			<WitnessSuccessStep
				witnessEmail={witnessBeforeSigning.witnessEmail}
				inviteFailed={witnessBeforeSigning.inviteFailed}
				isResending={witnessBeforeSigning.isInviting}
				onResend={witnessBeforeSigning.retryInvite}
				onDone={exitSigningToList}
			/>
		);
	}

	if (view === SignersTabView.SIGNING && activeSigner) {
		return (
			<SigningPanel
				status={eidEasyStatus}
				challengeCode={eidEasyChallengeCode}
				error={eidEasyError}
				selectedMethod={eidEasySelectedMethod}
				allowedMethods={methodsForSecurityLevel(
					activeSigner.effectiveSecurityLevel,
					activeSigner.roleType,
					{ enableSimpleSign: !webIsProduction }
				)}
				disableSavedSignature={activeSigner.roleType === DocumentSigningRoleType.WITNESS}
				hideStepHeader
				onClose={exitSigningToList}
				onSelectMethod={eidEasySelectMethod}
				onGoBack={eidEasyGoBack}
				onConfirmSmartId={(idcode, countryCode) => {
					void eidEasyConfirmSmartId(idcode, countryCode);
				}}
				onConfirmFreja={(email) => {
					void eidEasyConfirmFreja(email);
				}}
				onConfirmSignature={(signatureDataUrl, source) => {
					void eidEasyConfirmSignature(signatureDataUrl, source);
				}}
				onSignatureConfirmed={(sig) => {
					void eidEasyOnSignatureConfirmed(sig);
				}}
				onConfirmOtp={eidEasyConfirmOtp}
				onSubmitOtpCode={(code) => {
					void eidEasySubmitOtpCode(code);
				}}
				onResendOtpCode={eidEasyResendOtpCode}
				otpVerifying={eidEasyOtpVerifying}
				otpCodeError={eidEasyOtpCodeError}
				otpChannel={eidEasyOtpChannel}
				otpMaskedContact={eidEasyOtpMaskedContact}
				successDetails={signingSuccessDetails}
			/>
		);
	}

	return (
		<Box mt="0.5rem" pb="1rem">
			<Flex direction="column" gap="1rem" pl="1rem" mr="1rem">
				<AddInvestorAction
					dealId={dealId}
					programmeId={programmeId}
					documentWorkflowInstanceId={documentWorkflowInstanceId}
					onAdded={refetchDocument}
				/>
				<AddCounterpartyAction
					dealId={dealId}
					programmeId={programmeId}
					documentWorkflowInstanceId={documentWorkflowInstanceId}
					assignableUsers={counterpartyAssignableUsers}
					onAdded={handleCounterpartyAdded}
				/>
				{applyDateAction.visible && (
					<Flex direction="column" gap="0.25rem" align="flex-start">
						<Button
							variant="default"
							disabled={!applyDateAction.enabled}
							onClick={() => setApplyDateModalOpen(true)}
							data-testid={TEST_ELEMENT_IDS.DOCUMENT_SIGNERS_APPLY_DATE_BUTTON}
						>
							{tMipManage('documents_page.apply_date.signers.action_button')}
						</Button>
						{!applyDateAction.enabled && (
							<Text size="0.75rem" c="gray.6">
								{tMipManage(
									'documents_page.apply_date.signers.awaiting_signatures'
								)}
							</Text>
						)}
						{/* Mounted only when the action is live — a disabled button cannot open it. */}
						{applyDateAction.enabled && (
							<ApplyDateModal
								instanceId={documentWorkflowInstanceId}
								opened={applyDateModalOpen}
								onClose={() => setApplyDateModalOpen(false)}
								onApplied={onDateApplied}
							/>
						)}
					</Flex>
				)}
				{!documentProgress.isComplete && (
					<DocumentSignersBanner
						signaturesOutstanding={documentProgress.signaturesOutstanding}
						signersNeedingAssignment={documentProgress.signersNeedingAssignment}
						signed={documentProgress.signed}
						total={documentProgress.total}
					/>
				)}
				{signerWithRepresentativeGroups.map((group) => {
					const isCounterpartyGroup =
						group.groupType === DocumentSigningRoleType.COUNTERPARTY;

					const isAssigningThisGroup = assigningSlot?.roleId === group.roleId;
					const isUnAssignmentGroup = unAssigningRoleId === group.roleId;

					const isAssignmentPending =
						assignSignerMutation.isPending ||
						(isActionPending &&
							pendingSignerAssignmentAction?.action ===
								PendingDocumentActionType.ASSIGN &&
							pendingSignerAssignmentAction.roleId === group.roleId);

					const isUnAssignmentPending =
						unassignSignerMutation.isPending ||
						(isActionPending &&
							pendingSignerAssignmentAction?.action ===
								PendingDocumentActionType.UNASSIGN &&
							pendingSignerAssignmentAction.roleId === group.roleId);

					// A sibling slot on this role may hold the same person as an email-invited
					// authorised signatory rather than a platform-user assignment — exclude by
					// email too
					const occupiedEmails = buildOccupiedEmailSet(group.signers);

					const assignUserOptions = (() => {
						if (!programmeUsers || !isAssigningThisGroup) {
							return [];
						}

						const assignedUserIds = new Set(
							group.signers
								.filter((signer) => signer.assigned && signer.userId)
								.map((signer) => signer.userId)
						);

						return programmeUsers
							.filter(
								(user) =>
									!assignedUserIds.has(user.id) &&
									!occupiedEmails.has(normaliseEmail(user.email))
							)
							.map((user) => ({
								value: user.id,
								label: `${user.firstName} ${user.lastName}`,
								email: user.email,
							}));
					})();

					const currentUser = programmeUsers?.find((u) => u.id === currentUserId);

					const currentUserCanClaim = canCurrentUserClaimCounterpartySlot({
						isCounterpartyGroup,
						currentUser,
						signers: group.signers,
					});

					return (
						<SignerGroupSection
							key={group.roleId}
							group={group}
							documentSignatureType={documentDetails.signatureType}
							documentSigningScope={documentDetails.signingScope}
							documentESignProvider={documentDetails.eSignProvider}
							currentUserId={currentUserId}
							isSignerUpdatePending={isSignerUpdatePending}
							isEidEasyDocument={isEidEasyDocument}
							documentFullySigned={documentFullySigned}
							workflowStatus={documentDetails.workflowStatus}
							onSignClick={
								isCounterpartyGroup
									? () => handleSignClickForGroup(group)
									: undefined
							}
							onAssignClick={(slotIndex) =>
								handleAssignClick(group.roleId, slotIndex)
							}
							onUnassignClick={() => handleUnassignClick(group.roleId)}
							assigningSlotIndex={
								isAssigningThisGroup ? assigningSlot.slotIndex : null
							}
							isUnassignmentGroup={isUnAssignmentGroup}
							assignUserOptions={assignUserOptions}
							isLoadingUsers={isLoadingUsers}
							isAssignmentPending={isAssignmentPending}
							isUnassignmentPending={isUnAssignmentPending}
							unassignFormLabels={{
								placeholder: tMipManage(
									'documents_page.unassign_signer_form.placeholder'
								),
								confirmLabel: tMipManage(
									'documents_page.unassign_signer_form.confirm_label'
								),
								nothingFoundMessage: tMipManage(
									'documents_page.unassign_signer_form.nothing_found_message'
								),
							}}
							eSignInitiated={eidEasyInProgress}
							witnessRequirementLock={documentDetails.witnessRequirementLock}
							isCounterpartySigningBlocked={isCounterpartySigningBlocked}
							onInviteWitness={handleInviteWitness}
							onInviteAuthorisedSignatory={
								authorisedSignatoryEnabledForDocument
									? handleInviteAuthorisedSignatory
									: undefined
							}
							onCancelAuthorisedSignatory={handleCancelAuthorisedSignatory}
							onResendWitnessInvitation={handleResendWitnessInvitation}
							onResendAuthorisedSignatoryInvitation={
								authorisedSignatoryEnabledForDocument
									? handleResendAuthorisedSignatoryInvitation
									: undefined
							}
							resendingSignatureId={resendingSignatureId}
							onAssignConfirm={(userId) => handleAssignConfirm(group.roleId, userId)}
							onAssignCancel={handleAssignCancel}
							onUnassignConfirm={(userId) =>
								handleUnassignConfirm(group.roleId, userId)
							}
							onUnassignCancel={handleUnassignCancel}
							status={getGroupStatus(group.roleId)}
							currentUserCanClaim={currentUserCanClaim}
							onClaim={() => handleClaimSlot(group.roleId)}
							isClaimPending={isAssignmentPending && assigningSlot === null}
							claimEnabled={claimEnabledForDocument}
							onClaimSigner={onClaimSigner}
							claimingSignerId={claimingSignerId}
							onSignClaimedSigner={(signer, signingRoleType) =>
								handleSignClaimedSigner(signer, signingRoleType, group)
							}
							documentSignerCount={documentSignerCount}
							documentPartyCount={documentPartyCount}
							hasOutstandingSignatures={documentHasOutstandingSignatures}
							onRemoveSigner={setRemovalTarget}
						/>
					);
				})}
			</Flex>

			<RemoveSignerAction
				dealId={dealId}
				programmeId={programmeId}
				documentWorkflowInstanceId={documentWorkflowInstanceId}
				target={removalTarget}
				onClose={() => setRemovalTarget(null)}
			/>

			<WetInkSigningModal
				opened={signingModalOpen}
				onClose={handleModalClose}
				onDownloadContract={handleDownloadContract}
				onSave={handleSave}
				isSaving={wetInkSignDocumentWorkflowIsLoading}
				selectedFile={selectedFile}
				onFileSelect={setSelectedFile}
				onFileRemove={() => setSelectedFile(undefined)}
				isPrefillable={false}
			/>

			<WitnessEmailModal
				isOpen={witnessModalOpen}
				isLoading={inviteWitnessMutation.isPending}
				initialEmail={witnessInitialEmail}
				onClose={() => {
					setWitnessModalOpen(false);
					setWitnessParentSignatureId(null);
				}}
				onSubmit={handleWitnessSubmit}
			/>

			<AuthorisedSignatoryModal
				isOpen={authorisedSignatoryModalOpen}
				isLoading={inviteAuthorisedSignatoryMutation.isPending}
				initialEmail={authorisedSignatoryInitialEmail}
				initialFirstName={authorisedSignatoryInitialFirstName}
				initialLastName={authorisedSignatoryInitialLastName}
				onClose={() => {
					setAuthorisedSignatoryModalOpen(false);
					setAuthorisedSignatorySignatureId(null);
				}}
				onSubmit={handleAuthorisedSignatorySubmit}
			/>

			{cancelSignatoryTarget && (
				<CancelAuthorisedSignatoryModal
					dealId={dealId}
					programmeId={programmeId}
					documentWorkflowInstanceId={documentWorkflowInstanceId}
					signatureId={cancelSignatoryTarget.signatureId}
					signatoryName={cancelSignatoryTarget.signatoryName}
					opened
					onClose={() => setCancelSignatoryTarget(null)}
				/>
			)}
		</Box>
	);
};
